import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Spinner,
  Flex,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  Switch,
  NumberInput,
  NumberInputField,
  NumberIncrementStepper,
  NumberDecrementStepper,
  NumberInputStepper,
  Badge,
  InputGroup,
  InputLeftElement,
  Textarea,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
  SimpleGrid,
  Card,
  CardBody,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { 
  FaFlask, 
  FaSignOutAlt, 
  FaVial, 
  FaCheckCircle, 
  FaClock, 
  FaEye,
  FaSearch,
  FaSave,
  FaEdit,
  FaArrowLeft
} from 'react-icons/fa';

const LabTestCosts = () => {
  const [labSession, setLabSession] = useState(null);
  const [tests, setTests] = useState([]);
  const [panels, setPanels] = useState([]);
  const [testOfferings, setTestOfferings] = useState([]);
  const [panelOfferings, setPanelOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editingItem, setEditingItem] = useState(null);
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const sessionData = localStorage.getItem('labSession');
    if (!sessionData) {
      navigate('/lab-portal/login');
      return;
    }

    try {
      const parsedSession = JSON.parse(sessionData);
      setLabSession(parsedSession);
      fetchData(parsedSession.id);
    } catch (error) {
      navigate('/lab-portal/login');
    }
  }, [navigate]);

  const fetchData = async (labId) => {
    setLoading(true);
    try {
      // Fetch all tests and panels from master directory
      const { data: testsData, error: testsError } = await supabase
        .from('tests')
        .select('*')
        .order('name');

      const { data: panelsData, error: panelsError } = await supabase
        .from('panels')
        .select('*')
        .order('name');

      if (testsError) throw testsError;
      if (panelsError) throw panelsError;

      // Fetch lab's current test offerings
      const { data: testOfferingsData, error: testOfferingsError } = await supabase
        .from('lab_test_offerings')
        .select('*')
        .eq('lab_id', labId);

      // Fetch lab's current panel offerings
      const { data: panelOfferingsData, error: panelOfferingsError } = await supabase
        .from('lab_panel_offerings')
        .select('*')
        .eq('lab_id', labId);

      if (testOfferingsError) throw testOfferingsError;
      if (panelOfferingsError) throw panelOfferingsError;

      setTests(testsData || []);
      setPanels(panelsData || []);
      setTestOfferings(testOfferingsData || []);
      setPanelOfferings(panelOfferingsData || []);

    } catch (error) {
      toast({
        title: 'Error fetching data',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getTestOffering = (testId) => {
    return testOfferings.find(offering => offering.test_id === testId) || {
      is_offered: false,
      lab_test_code: '',
      lab_cash_price: null,
      lab_notes: ''
    };
  };

  const getPanelOffering = (panelId) => {
    return panelOfferings.find(offering => offering.panel_id === panelId) || {
      is_offered: false,
      lab_panel_code: '',
      lab_cash_price: null,
      lab_notes: ''
    };
  };

  const handleTestOfferingChange = async (testId, field, value) => {
    try {
      const currentOffering = getTestOffering(testId);
      const updatedOffering = { ...currentOffering, [field]: value };

      // Upsert the offering
      const { error } = await supabase
        .from('lab_test_offerings')
        .upsert({
          lab_id: labSession.id,
          test_id: testId,
          ...updatedOffering
        }, { onConflict: 'lab_id,test_id' });

      if (error) throw error;

      // Update local state
      setTestOfferings(prev => {
        const index = prev.findIndex(offering => offering.test_id === testId);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = { ...updated[index], [field]: value };
          return updated;
        } else {
          return [...prev, {
            lab_id: labSession.id,
            test_id: testId,
            ...updatedOffering
          }];
        }
      });

      if (field === 'is_offered') {
        toast({
          title: value ? 'Test enabled' : 'Test disabled',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }

    } catch (error) {
      toast({
        title: 'Error updating test offering',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handlePanelOfferingChange = async (panelId, field, value) => {
    try {
      const currentOffering = getPanelOffering(panelId);
      const updatedOffering = { ...currentOffering, [field]: value };

      // Upsert the offering
      const { error } = await supabase
        .from('lab_panel_offerings')
        .upsert({
          lab_id: labSession.id,
          panel_id: panelId,
          ...updatedOffering
        }, { onConflict: 'lab_id,panel_id' });

      if (error) throw error;

      // Update local state
      setPanelOfferings(prev => {
        const index = prev.findIndex(offering => offering.panel_id === panelId);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = { ...updated[index], [field]: value };
          return updated;
        } else {
          return [...prev, {
            lab_id: labSession.id,
            panel_id: panelId,
            ...updatedOffering
          }];
        }
      });

      if (field === 'is_offered') {
        toast({
          title: value ? 'Panel enabled' : 'Panel disabled',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }

    } catch (error) {
      toast({
        title: 'Error updating panel offering',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleBulkSave = async () => {
    setSaving(true);
    try {
      toast({
        title: 'All changes saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error saving changes',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditItem = (item, type) => {
    setEditingItem({ ...item, type });
    onEditOpen();
  };

  const handleSaveEdit = async () => {
    try {
      if (editingItem.type === 'test') {
        await handleTestOfferingChange(editingItem.id, 'lab_test_code', editingItem.lab_test_code);
        await handleTestOfferingChange(editingItem.id, 'lab_cash_price', editingItem.lab_cash_price);
        await handleTestOfferingChange(editingItem.id, 'lab_notes', editingItem.lab_notes);
      } else {
        await handlePanelOfferingChange(editingItem.id, 'lab_panel_code', editingItem.lab_panel_code);
        await handlePanelOfferingChange(editingItem.id, 'lab_cash_price', editingItem.lab_cash_price);
        await handlePanelOfferingChange(editingItem.id, 'lab_notes', editingItem.lab_notes);
      }
      onEditClose();
      setEditingItem(null);
    } catch (error) {
      toast({
        title: 'Error saving item',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const filteredTests = tests.filter(test => {
    const matchesSearch = test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (test.description && test.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || test.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredPanels = panels.filter(panel => {
    const matchesSearch = panel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (panel.description && panel.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || panel.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', ...new Set([...tests.map(t => t.category), ...panels.map(p => p.category)].filter(Boolean))];

  const TestRow = ({ test, offering }) => (
    <Tr>
      <Td>
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">{test.name}</Text>
          {test.description && (
            <Text fontSize="sm" color="gray.600" noOfLines={2}>
              {test.description}
            </Text>
          )}
        </VStack>
      </Td>
      <Td>
        <Badge colorScheme="blue" variant="subtle">Test</Badge>
      </Td>
      <Td>
        <Switch
          isChecked={offering.is_offered}
          onChange={(e) => handleTestOfferingChange(test.id, 'is_offered', e.target.checked)}
          colorScheme="green"
        />
      </Td>
      <Td>
        <Input
          size="sm"
          placeholder="Lab code"
          value={offering.lab_test_code || ''}
          onChange={(e) => handleTestOfferingChange(test.id, 'lab_test_code', e.target.value)}
          isDisabled={!offering.is_offered}
        />
      </Td>
      <Td>
        <NumberInput
          size="sm"
          min={0}
          precision={2}
          value={offering.lab_cash_price || ''}
          onChange={(value) => handleTestOfferingChange(test.id, 'lab_cash_price', parseFloat(value) || null)}
          isDisabled={!offering.is_offered}
        >
          <NumberInputField placeholder="$0.00" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </Td>
      <Td>
        <Button
          size="sm"
          leftIcon={<FaEdit />}
          onClick={() => handleEditItem({ ...test, ...offering }, 'test')}
          isDisabled={!offering.is_offered}
        >
          Edit
        </Button>
      </Td>
    </Tr>
  );

  const PanelRow = ({ panel, offering }) => (
    <Tr>
      <Td>
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">{panel.name}</Text>
          {panel.description && (
            <Text fontSize="sm" color="gray.600" noOfLines={2}>
              {panel.description}
            </Text>
          )}
        </VStack>
      </Td>
      <Td>
        <Badge colorScheme="purple" variant="subtle">Panel</Badge>
      </Td>
      <Td>
        <Switch
          isChecked={offering.is_offered}
          onChange={(e) => handlePanelOfferingChange(panel.id, 'is_offered', e.target.checked)}
          colorScheme="green"
        />
      </Td>
      <Td>
        <Input
          size="sm"
          placeholder="Lab code"
          value={offering.lab_panel_code || ''}
          onChange={(e) => handlePanelOfferingChange(panel.id, 'lab_panel_code', e.target.value)}
          isDisabled={!offering.is_offered}
        />
      </Td>
      <Td>
        <NumberInput
          size="sm"
          min={0}
          precision={2}
          value={offering.lab_cash_price || ''}
          onChange={(value) => handlePanelOfferingChange(panel.id, 'lab_cash_price', parseFloat(value) || null)}
          isDisabled={!offering.is_offered}
        >
          <NumberInputField placeholder="$0.00" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </Td>
      <Td>
        <Button
          size="sm"
          leftIcon={<FaEdit />}
          onClick={() => handleEditItem({ ...panel, ...offering }, 'panel')}
          isDisabled={!offering.is_offered}
        >
          Edit
        </Button>
      </Td>
    </Tr>
  );

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" minH="50vh">
          <Spinner size="xl" />
        </Flex>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.200" py={4}>
        <Container maxW="container.xl">
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Button
                leftIcon={<FaArrowLeft />}
                variant="ghost"
                onClick={() => navigate('/lab-portal/dashboard')}
              >
                Back to Dashboard
              </Button>
              <Icon as={FaFlask} boxSize={6} color="blue.500" />
              <VStack align="start" spacing={0}>
                <Heading size="md">Test Costs Management</Heading>
                <Text fontSize="sm" color="gray.600">{labSession?.name}</Text>
              </VStack>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
          {/* Search and Filters */}
          <Card>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search tests and panels..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </Select>
                <Button
                  leftIcon={<FaSave />}
                  colorScheme="blue"
                  onClick={handleBulkSave}
                  isLoading={saving}
                  loadingText="Saving..."
                >
                  Save All Changes
                </Button>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Tests and Panels Table */}
          <Card>
            <CardBody>
              <Tabs>
                <TabList>
                  <Tab>Individual Tests ({filteredTests.length})</Tab>
                  <Tab>Test Panels ({filteredPanels.length})</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel px={0}>
                    <Box overflowX="auto">
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Test Name</Th>
                            <Th>Type</Th>
                            <Th>Offered</Th>
                            <Th>Your Code</Th>
                            <Th>Your Price</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredTests.map(test => (
                            <TestRow
                              key={test.id}
                              test={test}
                              offering={getTestOffering(test.id)}
                            />
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </TabPanel>

                  <TabPanel px={0}>
                    <Box overflowX="auto">
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Panel Name</Th>
                            <Th>Type</Th>
                            <Th>Offered</Th>
                            <Th>Your Code</Th>
                            <Th>Your Price</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredPanels.map(panel => (
                            <PanelRow
                              key={panel.id}
                              panel={panel}
                              offering={getPanelOffering(panel.id)}
                            />
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </CardBody>
          </Card>
        </VStack>
      </Container>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Edit {editingItem?.type === 'test' ? 'Test' : 'Panel'}: {editingItem?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Your Code</FormLabel>
                <Input
                  value={editingItem?.type === 'test' ? (editingItem?.lab_test_code || '') : (editingItem?.lab_panel_code || '')}
                  onChange={(e) => {
                    const field = editingItem?.type === 'test' ? 'lab_test_code' : 'lab_panel_code';
                    setEditingItem(prev => ({ ...prev, [field]: e.target.value }));
                  }}
                  placeholder="Enter your internal code"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Your Price ($)</FormLabel>
                <NumberInput
                  min={0}
                  precision={2}
                  value={editingItem?.lab_cash_price || ''}
                  onChange={(value) => setEditingItem(prev => ({ ...prev, lab_cash_price: parseFloat(value) || null }))}
                >
                  <NumberInputField placeholder="0.00" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={editingItem?.lab_notes || ''}
                  onChange={(e) => setEditingItem(prev => ({ ...prev, lab_notes: e.target.value }))}
                  placeholder="Any notes about this test for your lab"
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LabTestCosts; 